import { useState, useEffect, useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { useCorpse } from './useCorpse';
import { captureCanvas } from '@utils/canvasSnapshot';
import { uploadGameImage, buildSubmissionPath } from '@utils/imageStorage';
import { useFirebase } from '@/App';
import { getZonePreset, getZoneHeight, ZONE_COLOUR_CLASSES } from './corpseAssignment';
import { ChevronRight } from 'lucide-react';

/**
 * Dramatic creature reveal — the showpiece moment of Exquisite Corpse.
 * Zone snapshots are revealed one by one with staggered animations.
 * Supports 3, 4, or 5 zones.
 */
export default function CorpseReveal({ stageRef }) {
  const { state } = useApp();
  const { game, user } = state;
  const firebase = useFirebase();
  const { advanceFromReveal } = useCorpse();

  const [showCredits, setShowCredits] = useState(false);
  const [hasCaptured, setHasCaptured] = useState(false);

  const currentCreature = game.currentCreature ?? 0;
  const creature = game.creatures?.[currentCreature];
  const totalCreatures = game.totalCreatures ?? 1;
  const hasMore = currentCreature < totalCreatures - 1;
  const zoneCount = game.zoneCount || 3;
  const preset = getZonePreset(zoneCount);
  const zoneH = getZoneHeight(zoneCount);

  // Capture full creature snapshot once on mount — upload to Storage
  useEffect(() => {
    if (hasCaptured || !stageRef) return;

    const timer = setTimeout(async () => {
      const dataUrl = captureCanvas(stageRef);
      if (dataUrl) {
        const path = buildSubmissionPath(state.room.code,
          `corpse/creature${currentCreature}`, 'full');
        const imageUrl = await uploadGameImage(dataUrl, path);
        if (imageUrl) {
          await firebase.updateGameState({
            [`creatures/${currentCreature}/fullSnapshot`]: imageUrl,
          });
        }
      }
      setHasCaptured(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [stageRef, firebase, currentCreature, hasCaptured, state.room.code]);

  // Show credits after all zones have animated in
  // Stagger: each zone takes ~1.2s with 0.8s gaps
  const creditDelay = Math.max(3500, 1000 + zoneCount * 800);
  useEffect(() => {
    const timer = setTimeout(() => setShowCredits(true), creditDelay);
    return () => clearTimeout(timer);
  }, [creditDelay]);

  const handleAdvance = useCallback(() => {
    advanceFromReveal();
  }, [advanceFromReveal]);

  // Build zone data from creature
  const zones = preset.map((p, i) => ({
    ...p,
    snapshot: creature?.zones?.[i]?.snapshot,
    nickname: creature?.zones?.[i]?.nickname || 'Unknown',
    assignedTo: creature?.zones?.[i]?.assignedTo,
  }));

  // Staggered animation: first slides down, last slides up, middles fade in
  const getAnimClass = (i) => {
    const delay = (i * 0.8).toFixed(1);
    if (i === 0) return `animate-[slideDown_0.8s_ease-out_forwards]`;
    if (i === zoneCount - 1) return `animate-[slideUp_0.8s_ease-out_${delay}s_both]`;
    return `animate-[fadeIn_0.8s_ease-out_${delay}s_both]`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="max-w-sm w-full mx-4">
        {/* Creature counter */}
        {totalCreatures > 1 && (
          <p className="text-center text-sm text-white/40 font-display mb-4">
            Creature {currentCreature + 1} of {totalCreatures}
          </p>
        )}

        {/* Stacked zone reveals */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {zones.map((zone, i) => {
            const colours = ZONE_COLOUR_CLASSES[zone.colour];
            return (
              <div key={i} className={`relative ${getAnimClass(i)}`}>
                {zone.snapshot ? (
                  <img
                    src={zone.snapshot}
                    alt={`${zone.name} zone`}
                    className="w-full h-auto block bg-canvas"
                    style={{ aspectRatio: `2000 / ${Math.round(zoneH)}` }}
                  />
                ) : (
                  <div
                    className="w-full bg-slate-100 flex items-center justify-center"
                    style={{ aspectRatio: `2000 / ${Math.round(zoneH)}` }}
                  >
                    <p className="text-slate-300 font-display text-sm">No drawing</p>
                  </div>
                )}
                {/* Zone label overlay */}
                <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-display font-medium ${colours.highlight} text-white`}>
                  {zone.emoji} {zone.name}
                </div>
              </div>
            );
          })}
        </div>

        {/* "You made this together" moment */}
        <div className={`text-center mt-6 animate-[fadeIn_1s_ease-out_${(zoneCount * 0.8 + 0.5).toFixed(1)}s_both]`}>
          <p className="text-2xl font-bold text-white font-display">
            You made this creature together
          </p>
        </div>

        {/* Credits: who drew what */}
        {showCredits && (
          <div className="mt-4 space-y-1.5 animate-[fadeIn_0.5s_ease-out]">
            {zones.map((zone, i) => {
              const colours = ZONE_COLOUR_CLASSES[zone.colour];
              return (
                <div key={i} className="flex items-center justify-center gap-2 text-sm font-display text-white/60">
                  <span className={colours.text}>{zone.name}</span>
                  <span className="text-white/20">—</span>
                  <span className="text-white/80">
                    {zone.assignedTo === user.id ? 'You' : zone.nickname}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Host controls */}
        {user.isHost && showCredits && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleAdvance}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors"
            >
              {hasMore ? (
                <>Next Creature <ChevronRight className="w-4 h-4" /></>
              ) : (
                'View Gallery'
              )}
            </button>
          </div>
        )}
      </div>

      {/* CSS keyframes */}
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
